import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './UserLifecycle.css';
import API_URL from '../config';

const DEPARTMENTS = ['Finance', 'HR', 'IT', 'Operations', 'Security'];

const UserLifecycle = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('joiner');
  const [oktaUsers, setOktaUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [joinerForm, setJoinerForm] = useState({
    firstName: '', lastName: '', email: '', department: 'Finance', password: ''
  });

  const [moverForm, setMoverForm] = useState({
    userId: '', fromGroup: 'Finance', toGroup: 'HR'
  });
  const [userGroups, setUserGroups] = useState([])

  const [leaverForm, setLeaverForm] = useState({
    userId: '', fullName: ''
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/okta/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setOktaUsers(data);
      } catch (err) {
        console.error('Failed to fetch Okta users:', err);
      }
    };
    fetchUsers();
  }, [token]);

  const clearMessages = () => { setMessage(''); setError(''); };

  const handleJoiner = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/lifecycle/joiner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(joinerForm)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setJoinerForm({ firstName: '', lastName: '', email: '', department: 'Finance', password: '' });
    } catch (err) {
      setError('Failed to provision user');
    } finally {
      setLoading(false);
    }
  };

  const handleMover = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/lifecycle/mover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(moverForm)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
    } catch (err) {
      setError('Failed to move user');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaver = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/lifecycle/leaver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(leaverForm)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setLeaverForm({ userId: '', fullName: '' });
    } catch (err) {
      setError('Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const canJoin = ['ADMIN', 'PROVISIONER'].includes(user?.role);
  const canMove = ['ADMIN', 'PROVISIONER', 'DEPROVISIONER'].includes(user?.role);
  const canLeave = ['ADMIN', 'DEPROVISIONER'].includes(user?.role);

  return (
    <Layout>
      <div className="page-header" style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#003366' }}>User Lifecycle</h1>
      </div>

      <div className="lifecycle-tabs">
        {canJoin && <button className={`tab-btn ${activeTab === 'joiner' ? 'active' : ''}`} onClick={() => { setActiveTab('joiner'); clearMessages(); }}>Joiner</button>}
        {canMove && <button className={`tab-btn ${activeTab === 'mover' ? 'active' : ''}`} onClick={() => { setActiveTab('mover'); clearMessages(); }}>Mover</button>}
        {canLeave && <button className={`tab-btn ${activeTab === 'leaver' ? 'active' : ''}`} onClick={() => { setActiveTab('leaver'); clearMessages(); }}>Leaver</button>}
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      {activeTab === 'joiner' && canJoin && (
        <div className="lifecycle-card">
          <h2>Provision New User</h2>
          <p>Create a new ABC Corp employee account in Okta and assign them to their department group.</p>
          <form onSubmit={handleJoiner}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={joinerForm.firstName} onChange={e => setJoinerForm({ ...joinerForm, firstName: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={joinerForm.lastName} onChange={e => setJoinerForm({ ...joinerForm, lastName: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" value={joinerForm.email} onChange={e => setJoinerForm({ ...joinerForm, email: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Department</label>
              <select value={joinerForm.department} onChange={e => setJoinerForm({ ...joinerForm, department: e.target.value })}>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Temporary Password</label>
              <input type="password" value={joinerForm.password} onChange={e => setJoinerForm({ ...joinerForm, password: e.target.value })} required />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Provisioning...' : 'Provision User'}</button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'mover' && canMove && (
        <div className="lifecycle-card">
          <h2>Transfer User</h2>
          <p>Move an existing ABC Corp employee from one department to another in Okta.</p>
          <form onSubmit={handleMover}>
            <div className="form-group">
              <label>Select User</label>
              <select value={moverForm.userId} onChange={async e => {
                const userId = e.target.value;
                setMoverForm({ ...moverForm, userId });
                if (userId) {
                  try {
                    const res = await fetch(`${API_URL}/okta/users/${userId}/groups`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const groups = await res.json();
                    setUserGroups(groups);
                    if (groups.length > 0) {
                      setMoverForm(prev => ({ ...prev, userId, fromGroup: groups[0].name }));
                    }
                  } catch (err) {
                    console.error('Failed to fetch user groups:', err);
                  }
                } else {
                  setUserGroups([]);
                }
              }} required>
                <option value="">-- Select a user --</option>
                {oktaUsers.filter(u => u.status === 'ACTIVE').map(u => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                ))}
              </select>
            </div>
            {userGroups.length > 0 && (
              <div className="user-select-info">
                📋 Current groups: <strong>{userGroups.map(g => g.name).join(', ')}</strong>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>From Department</label>
                <select value={moverForm.fromGroup} onChange={e => setMoverForm({ ...moverForm, fromGroup: e.target.value })}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>To Department</label>
                <select value={moverForm.toGroup} onChange={e => setMoverForm({ ...moverForm, toGroup: e.target.value })}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Moving...' : 'Transfer User'}</button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'leaver' && canLeave && (
        <div className="lifecycle-card">
          <h2>Deactivate User</h2>
          <p>Suspend an ABC Corp employee account in Okta. This revokes all their app access immediately.</p>
          <form onSubmit={handleLeaver}>
            <div className="form-group">
              <label>Select User</label>
              <select value={leaverForm.userId} onChange={e => {
                const selected = oktaUsers.find(u => u.id === e.target.value);
                setLeaverForm({ userId: e.target.value, fullName: selected ? `${selected.firstName} ${selected.lastName}` : '' });
              }} required>
                <option value="">-- Select a user --</option>
                {oktaUsers.filter(u => u.status === 'ACTIVE').map(u => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                ))}
              </select>
            </div>
            {leaverForm.fullName && (
              <div className="user-select-info">
                ⚠️ You are about to deactivate <strong>{leaverForm.fullName}</strong>. This action will revoke all their Okta access immediately.
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="btn-danger" disabled={loading || !leaverForm.userId}>{loading ? 'Deactivating...' : 'Deactivate User'}</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default UserLifecycle;